import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { DialPhoneField } from "./DialPhoneField";
import { CommonToast } from "../../../common/Toast";
import { useCreateCallbackRequestMutation } from "../../../../store/Callback/callbackApiSlice";
import { OTHER_OPTION } from "../../../../constants/tradeVerticals";
import { cn } from "../../../common/u1/cn";
import { CONTROL, controlBorder, Field, MENU, MENU_ROW } from "./TradeField";
import { CheckIcon, ChevronDown } from "../../../../assets/ui/icons";

/* "Talk to our team" — the form at the foot of every Trade vertical page. */
export const CallbackForm = ({ product, options = [], isMember = false }) => {
  const { token } = useSelector((state) => state.auth);
  const [createCallbackRequest, { isLoading }] = useCreateCallbackRequestMutation();

  const [contactName, setContactName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [dial, setDial] = useState("+971");
  const [phone, setPhone] = useState("");
  const [need, setNeed] = useState("");
  const [note, setNote] = useState("");
  const [needOpen, setNeedOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const isOther = need === OTHER_OPTION;

  const reset = () => {
    setContactName("");
    setBusinessName("");
    setDial("+971");
    setPhone("");
    setNeed("");
    setNote("");
    setErrors({});
  };

  /* Transcribed rule for rule. */
  const validate = () => {
    const next = {};
    if (!contactName.trim()) next.contactName = "Please enter your name.";
    if (!businessName.trim()) next.businessName = "Please enter your business name.";
    if (!phone) next.phone = "Please enter a number we can call.";
    if (!need) next.need = "Please pick an option.";
    if (need === OTHER_OPTION && !note.trim()) next.note = "Please tell us a little more.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const clear = (key) => setErrors((prev) => ({ ...prev, [key]: undefined }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    if (!validate()) return;

    try {
      await createCallbackRequest({
        data: {
          contact_name: contactName.trim(),
          business_name: businessName.trim(),
          phone_number: `${dial} ${phone}`.trim(),
          product,
          need,
          note: isOther ? note.trim() : "",
          is_member: !!isMember,
        },
        token,
      }).unwrap();
      CommonToast.success(
        "Thanks — a member of our team will call you back, usually the same working day."
      );
      reset();
    } catch (err) {
      CommonToast.error(
        "Sorry, something went wrong sending your request. Please try again."
      );
    }
  };

  const selectNeed = (option) => {
    setNeed(option);
    setNeedOpen(false);
    clear("need");
  };

  /* The live dropdown closes only on the click-away layer. */
  useEffect(() => {
    if (!needOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setNeedOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [needOpen]);

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 rounded-xl border border-[var(--form-line)] bg-white p-5"
    >
      <Field id="callback-name" label="Your name" error={errors.contactName}>
        <input
          id="callback-name"
          placeholder="e.g. Marcus Sterling"
          value={contactName}
          onChange={(e) => {
            setContactName(e.target.value);
            clear("contactName");
          }}
          aria-invalid={errors.contactName ? true : undefined}
          aria-describedby={errors.contactName ? "callback-name-error" : undefined}
          className={cn(CONTROL, controlBorder(errors.contactName))}
        />
      </Field>

      <Field id="callback-business" label="Business name" error={errors.businessName}>
        <input
          id="callback-business"
          placeholder="Your business"
          value={businessName}
          onChange={(e) => {
            setBusinessName(e.target.value);
            clear("businessName");
          }}
          aria-invalid={errors.businessName ? true : undefined}
          aria-describedby={errors.businessName ? "callback-business-error" : undefined}
          className={cn(CONTROL, controlBorder(errors.businessName))}
        />
      </Field>

      <div className="flex flex-col gap-1.5">
        <DialPhoneField
          dial={dial}
          onDialChange={setDial}
          phone={phone}
          onPhoneChange={(v) => {
            setPhone(v);
            clear("phone");
          }}
          error={!!errors.phone}
        />
        {errors.phone && (
          <span role="alert" className="text-[12px] text-[var(--danger)] font-sans">
            {errors.phone}
          </span>
        )}
      </div>

      <Field id="callback-need" label="How can we help?" error={errors.need}>
        <div className="relative">
          <button
            type="button"
            id="callback-need"
            onClick={() => setNeedOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={needOpen}
            /* A <label for> does not name a button, so the control states its
               own name rather than being announced as just its value. */
            aria-label="How can we help?"
            aria-invalid={errors.need ? true : undefined}
            className={cn(
              CONTROL,
              controlBorder(errors.need),
              "flex cursor-pointer items-center justify-between gap-2.5"
            )}
          >
            <span
              className={cn(
                "truncate",
                need ? "font-medium text-[var(--navy)]" : "text-[var(--trade-micro)]"
              )}
            >
              {need || "Select an option"}
            </span>
            <span className="text-[var(--trade-micro)]">
              <ChevronDown open={needOpen} />
            </span>
          </button>

          {needOpen && (
            <>
              <div
                aria-hidden="true"
                onClick={() => setNeedOpen(false)}
                className="fixed inset-0 z-[35]"
              />
              <div role="listbox" className={cn(MENU, "right-0")}>
                {options.map((option) => (
                  <button
                    type="button"
                    key={option}
                    role="option"
                    aria-selected={need === option}
                    onClick={() => selectNeed(option)}
                    className={MENU_ROW}
                  >
                    <span className="grow basis-0 text-left">{option}</span>
                    {need === option && <CheckIcon size={16} stroke="var(--orange)" />}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </Field>

      {/* Only "Other" needs a sentence from the user. */}
      {isOther && (
        <Field id="callback-note" label="Tell us more" error={errors.note}>
          <textarea
            id="callback-note"
            placeholder="A sentence or two about what you need."
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              clear("note");
            }}
            rows={3}
            aria-invalid={errors.note ? true : undefined}
            aria-describedby={errors.note ? "callback-note-error" : undefined}
            className={cn(CONTROL, controlBorder(errors.note), "resize-y")}
          />
        </Field>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className={cn(
          "mt-0.5 w-full rounded-[10px] bg-[var(--orange)] py-[13px] text-[14.5px] font-bold text-white font-sans",
          "transition-colors hover:bg-[var(--orange-hover)]",
          isLoading ? "cursor-not-allowed opacity-70" : "cursor-pointer"
        )}
      >
        {isLoading ? "Sending…" : "Talk to our team"}
      </button>
    </form>
  );
};

export default CallbackForm;
